import {useEffect, useRef, useState} from 'react';
import {AccessibilityInfo, findNodeHandle, View} from 'react-native';

import TouchableOpacity from '/component/base/TouchableOpacity';

import type {DropdownOption} from './BaseDropdown.types';

interface UseBaseDropdownProps {
  value: string;
  options: DropdownOption[];
  open?: boolean;
  onToggle?: () => void;
  dropdownKey?: string;
  disabled?: boolean;
}

export const useBaseDropdown = ({
  value,
  options,
  open: controlledOpen,
  onToggle: controlledToggle,
  disabled,
}: UseBaseDropdownProps) => {
  const triggerRef = useRef<View>(null);
  const optionRefs = useRef<
    Record<string, React.ElementRef<typeof TouchableOpacity> | null>
  >({});
  const hasSetInitialFocus = useRef(false);

  const [coords, setCoords] = useState({x: 0, y: 0, w: 0, h: 0});
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const toggle = () => {
    if (isControlled) {
      controlledToggle?.();
    } else {
      setInternalOpen(prev => !prev);
    }
  };

  const handleOpen = () => {
    if (disabled) return;

    triggerRef.current?.measureInWindow((x, y, width, height) => {
      setCoords({x, y, w: width, h: height});
      toggle();
    });
  };

  const updateCoords = () => {
    triggerRef.current?.measureInWindow((x, y, w, h) => {
      setCoords({x, y, w, h});
    });
  };

  useEffect(() => {
    if (isOpen && !hasSetInitialFocus.current) {
      const timeout = setTimeout(() => {
        const selectedValue = value || options[0]?.value;
        const targetRef = selectedValue
          ? optionRefs.current[selectedValue]
          : null;
        const node = targetRef ? findNodeHandle(targetRef) : null;

        if (node) {
          AccessibilityInfo.setAccessibilityFocus(node);
          hasSetInitialFocus.current = true;
        }
      }, 600);

      return () => clearTimeout(timeout);
    }

    if (!isOpen) {
      hasSetInitialFocus.current = false;
    }
  }, [isOpen]);

  return {
    triggerRef,
    optionRefs,
    coords,
    isOpen,
    isControlled,
    toggle,
    handleOpen,
    updateCoords,
  };
};
